import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useWebSocket } from './useWebSocket';
import { useClockControls } from './useClockControls';
import { useLineupWithStatus } from './useLineupWithStatus';
import { useWebSocketContext } from '../contexts/WebSocketContext';

const API_URL = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5000`;

export const useLiveMatch = (matchId) => {
  const navigate = useNavigate();
  const { socket } = useWebSocketContext();
  const {
    connected,
    joined,
    events,
    score,
    viewers,
    clock,
    createEvent,
    updateScore,
    deleteEvent
  } = useWebSocket(matchId);

  const {
    startClock,
    stopClock,
    resetClock,
    setTime,
    setInjuryTime,
    changeHalf
  } = useClockControls(matchId);

  const {
    homeTeam,
    awayTeam,
    activeCounts: initialCounts,
    loading: lineupLoading,
    refetch: refetchLineup
  } = useLineupWithStatus(matchId);

  // Local state for real-time counts (overrides initialCounts from HTTP)
  const [activeCounts, setActiveCounts] = useState(null);

  // Sync initial counts
  useEffect(() => {
    if (initialCounts) {
      setActiveCounts(initialCounts);
    }
  }, [initialCounts]);

  const [match, setMatch] = useState(null);
  const [lineupStatus, setLineupStatus] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);

  // Modal states
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    action: null,
    title: '',
    message: ''
  });

  // Substitution modal states
  const [substitutionModal, setSubstitutionModal] = useState({
    isOpen: false,
    outgoingPlayer: null
  });

  // Captain transfer modal states
  const [captainTransferModal, setCaptainTransferModal] = useState({
    isOpen: false,
    currentCaptain: null,
    pendingSubstitution: null,
    pendingEventType: null
  });

  const [captainAlert, setCaptainAlert] = useState(null);

  // Overlay control states
  const [scoreboardVisible, setScoreboardVisible] = useState(false);
  const [scoreboardTimer, setScoreboardTimer] = useState(null);
  const [scoreboardTimeout, setScoreboardTimeout] = useState(() => {
    const saved = localStorage.getItem('scoreboardTimeout');
    return saved ? parseInt(saved) : 30000;
  });
  const [formationsVisible, setFormationsVisible] = useState(false);
  const [logoVisible, setLogoVisible] = useState(false);

  // VAR modal state
  const [varModal, setVarModal] = useState({ isOpen: false, event: null });

  // Listen for captain removal and counts updated
  useEffect(() => {
    if (!joined) return;
    if (!socket) return;

    socket.on('match:captain:removed', (data) => {
      console.log('[Captain] Captain removed:', data);
      setCaptainAlert({
        teamId: data.teamId,
        playerName: data.playerName,
        reason: data.reason
      });
      setTimeout(() => refetchLineup(), 300);
    });

    socket.on('match:counts:updated', (data) => {
      console.log('[Counts] Updated:', data);
      setActiveCounts({
        homeTeam: data.homeTeam,
        awayTeam: data.awayTeam
      });
      setTimeout(() => refetchLineup(), 300);
    });

    return () => {
      if (socket) {
        socket.off('match:captain:removed');
        socket.off('match:counts:updated');
      }
    };
  }, [joined, socket, refetchLineup]);

  // Load match data
  useEffect(() => {
    loadMatchData();
    loadEventTypes();
  }, [matchId]);

  const loadEventTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/event-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('[useLiveMatch] Event types loaded:', response.data.data);
      setEventTypes(response.data.data);
    } catch (error) {
      console.error('Error loading event types:', error);
    }
  };

  const loadMatchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [matchRes, lineupRes] = await Promise.all([
        axios.get(`${API_URL}/api/matches/${matchId}`, { headers }),
        axios.get(`${API_URL}/api/lineups/${matchId}/status`, { headers })
      ]);

      setMatch(matchRes.data.data);
      setLineupStatus(lineupRes.data);
    } catch (error) {
      console.error('Error loading match data:', error);
    }
  };

  const lineupsComplete = () => {
    if (!lineupStatus) return false;
    return lineupStatus.homeTeam.starting >= 11 && lineupStatus.awayTeam.starting >= 11;
  };

  const captainsAssigned = () => {
    const homeCaptain = homeTeam.find(p => p.is_captain && p.eligibility.eligible);
    const awayCaptain = awayTeam.find(p => p.is_captain && p.eligibility.eligible);
    return homeCaptain && awayCaptain;
  };

  const handleStartMatch = async () => {
    if (!lineupsComplete()) {
      alert('Both teams need 11 starting players');
      return;
    }

    if (!captainsAssigned()) {
      alert('Both teams must have a captain assigned');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/matches/${matchId}/start`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );

      setMatch(response.data.data);
      setConfirmModal({ isOpen: false, action: null, title: '', message: '' });

      if (response.data.warnings) {
        alert('Match started with warnings:\n' + response.data.warnings.join('\n'));
      }
    } catch (error) {
      console.error('Error starting match:', error);
      alert(error.response?.data?.error || 'Failed to start match');
    }
  };

  const handleEndMatch = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/matches/${matchId}/end`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );

      setMatch(response.data.data);
      setConfirmModal({ isOpen: false, action: null, title: '', message: '' });
    } catch (error) {
      console.error('Error ending match:', error);
      alert(error.response?.data?.error || 'Failed to end match');
    }
  };

  const handleCancelMatch = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/matches/${matchId}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );

      setMatch(response.data.data);
      setConfirmModal({ isOpen: false, action: null, title: '', message: '' });
    } catch (error) {
      console.error('Error cancelling match:', error);
      alert(error.response?.data?.error || 'Failed to cancel match');
    }
  };

  const handleScoreUpdate = (home, away) => {
    updateScore(home, away);
  };

  const openPublicView = () => {
    window.open(`/watch/${matchId}`, '_blank', 'width=1200,height=800');
  };

  const openOBSView = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/settings/broadcast`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const w = res.data?.overlay_width || 1920;
      const h = res.data?.overlay_height || 1080;
      window.open(`/watch/${matchId}?layout=all-layers`, '_blank', `width=${w},height=${h}`);
    } catch (error) {
      console.error('Error fetching broadcast settings, using defaults:', error);
      window.open(`/watch/${matchId}?layout=all-layers`, '_blank', 'width=1920,height=1080');
    }
  };

  // Scoreboard toggle with auto-hide
  const toggleScoreboard = async () => {
    const newValue = !scoreboardVisible;
    setScoreboardVisible(newValue);

    if (scoreboardTimer) {
      clearTimeout(scoreboardTimer);
      setScoreboardTimer(null);
    }

    if (newValue && scoreboardTimeout > 0) {
      const timer = setTimeout(() => {
        setScoreboardVisible(false);
        toggleScoreboardAPI(false);
      }, scoreboardTimeout);
      setScoreboardTimer(timer);
    }

    await toggleScoreboardAPI(newValue);
  };

  const handleScoreboardTimeoutChange = (newTimeout) => {
    setScoreboardTimeout(newTimeout);
    localStorage.setItem('scoreboardTimeout', newTimeout.toString());
  };

  const toggleScoreboardAPI = async (visible) => {
    try {
      await axios.post(`${API_URL}/api/overlay/${matchId}/toggle`, {
        layerId: 'scoreboard-top',
        visible
      });
    } catch (error) {
      console.error('Error toggling scoreboard:', error);
    }
  };

  const toggleFormations = async () => {
    const newValue = !formationsVisible;
    setFormationsVisible(newValue);

    try {
      await axios.post(`${API_URL}/api/overlay/${matchId}/toggle`, {
        layerId: 'formations',
        visible: newValue
      });
    } catch (error) {
      console.error('Error toggling formations:', error);
    }
  };

  const toggleLogo = async () => {
    const newValue = !logoVisible;
    setLogoVisible(newValue);

    try {
      await axios.post(`${API_URL}/api/overlay/${matchId}/toggle`, {
        layerId: 'broadcaster-logo',
        visible: newValue
      });
    } catch (error) {
      console.error('Error toggling logo:', error);
    }
  };

  // Wrapper to auto-start match when starting clock
  const handleStartClock = async () => {
    if (match.status === 'scheduled') {
      try {
        const token = localStorage.getItem('token');
        await axios.post(
          `${API_URL}/api/matches/${matchId}/start`,
          {},
          { headers: { Authorization: `Bearer ${token}` }}
        );
        setMatch(prev => ({ ...prev, status: 'live' }));
      } catch (error) {
        console.error('Error auto-starting match:', error);
      }
    }
    startClock();
  };

  const handleQuickEvent = useCallback(async (eventType, playerPos) => {
    // Handle voluntary captain transfer
    if (eventType === 'captain_transfer') {
      const teamPlayers = playerPos.team_id === homeTeam[0]?.team_id ? homeTeam : awayTeam;
      const currentCaptain = teamPlayers.find(p => p.is_captain && p.eligibility.eligible);

      const playerName = `${playerPos.player.first_name} ${playerPos.player.last_name}`;
      const captainName = currentCaptain
        ? `${currentCaptain.player.first_name} ${currentCaptain.player.last_name}`
        : 'nobody';

      if (!window.confirm(`Transfer captaincy from ${captainName} to ${playerName}?`)) return;

      try {
        const token = localStorage.getItem('token');
        await axios.post(
          `${API_URL}/api/lineups/${matchId}/transfer-captain`,
          { newCaptainId: playerPos.id },
          { headers: { Authorization: `Bearer ${token}` }}
        );
        setTimeout(() => refetchLineup(), 300);
      } catch (error) {
        console.error('Error transferring captaincy:', error);
        alert('Failed to transfer captaincy');
      }
      return;
    }

    // Handle substitution with modal
    if (eventType === 'substitution') {
      if (playerPos.is_captain) {
        setCaptainTransferModal({
          isOpen: true,
          currentCaptain: playerPos,
          pendingSubstitution: playerPos
        });
        return;
      }

      setSubstitutionModal({
        isOpen: true,
        outgoingPlayer: playerPos
      });
      return;
    }

    // Handle red card - check if captain needs transfer
    if (eventType === 'red_card' && playerPos.is_captain) {
      setCaptainTransferModal({
        isOpen: true,
        currentCaptain: playerPos,
        pendingSubstitution: null,
        pendingEventType: 'red_card'
      });
      return;
    }

    // Handle yellow card - check if this would be 2nd yellow for captain
    if (eventType === 'yellow_card' && playerPos.is_captain) {
      const existingYellows = events.filter(e =>
        e.player_id === playerPos.player_id &&
        e.event_type === 'yellow_card'
      ).length;

      if (existingYellows === 1) {
        setCaptainTransferModal({
          isOpen: true,
          currentCaptain: playerPos,
          pendingSubstitution: null,
          pendingEventType: 'yellow_card'
        });
        return;
      }
    }

    if (!Array.isArray(eventTypes) || eventTypes.length === 0) {
      console.error('Event types not loaded yet');
      return;
    }

    const eventTypeObj = eventTypes.find(et => et.name === eventType);

    if (!eventTypeObj) {
      console.error('Event type not found:', eventType);
      return;
    }

    const eventData = {
      matchId,
      eventTypeId: eventTypeObj.id,
      teamId: playerPos.team_id,
      playerId: playerPos.player_id,
      minute: clock.minute,
      second: clock.second,
      half: clock.half
    };

    await createEvent(eventData);
    setTimeout(() => refetchLineup(), 300);
  }, [matchId, homeTeam, awayTeam, eventTypes, events, clock, createEvent, refetchLineup]);

  // Handle captain transfer
  const handleCaptainTransfer = async (newCaptainPlayerPos) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/lineups/${matchId}/transfer-captain`,
        { newCaptainId: newCaptainPlayerPos.id },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      const pendingPlayer = captainTransferModal.pendingSubstitution;
      const pendingEvent = captainTransferModal.pendingEventType;
      const oldCaptain = captainTransferModal.currentCaptain;

      setCaptainTransferModal({
        isOpen: false,
        currentCaptain: null,
        pendingSubstitution: null,
        pendingEventType: null
      });

      if (pendingPlayer) {
        setSubstitutionModal({
          isOpen: true,
          outgoingPlayer: pendingPlayer
        });
      } else if (pendingEvent === 'red_card' && oldCaptain) {
        const redCardType = eventTypes.find(et => et.name === 'red_card');
        if (redCardType) {
          const eventData = {
            matchId,
            eventTypeId: redCardType.id,
            teamId: oldCaptain.team_id,
            playerId: oldCaptain.player_id,
            minute: clock.minute,
            second: clock.second,
            half: clock.half
          };
          await createEvent(eventData);
          setTimeout(() => refetchLineup(), 500);
        }
      } else if (pendingEvent === 'yellow_card' && oldCaptain) {
        const yellowCardType = eventTypes.find(et => et.name === 'yellow_card');
        if (yellowCardType) {
          const eventData = {
            matchId,
            eventTypeId: yellowCardType.id,
            teamId: oldCaptain.team_id,
            playerId: oldCaptain.player_id,
            minute: clock.minute,
            second: clock.second,
            half: clock.half
          };
          await createEvent(eventData);
          setTimeout(() => refetchLineup(), 500);
        }
      } else {
        setTimeout(() => refetchLineup(), 500);
      }
    } catch (error) {
      console.error('Error transferring captaincy:', error);
      alert('Failed to transfer captaincy');
    }
  };

  // Handle substitution confirmation
  const handleSubstitutionConfirm = async (outgoingPlayer, incomingPlayer) => {
    try {
      const substitutionType = eventTypes.find(et => et.name === 'substitution');
      if (!substitutionType) {
        console.error('Substitution event type not found');
        return;
      }

      const eventData = {
        matchId,
        eventTypeId: substitutionType.id,
        teamId: outgoingPlayer.team_id,
        playerId: incomingPlayer.player_id,
        secondaryPlayerId: outgoingPlayer.player_id,
        minute: clock.minute,
        second: clock.second,
        half: clock.half
      };

      await createEvent(eventData);

      setSubstitutionModal({ isOpen: false, outgoingPlayer: null });
      setTimeout(() => refetchLineup(), 500);
    } catch (error) {
      console.error('Error creating substitution:', error);
      alert('Failed to create substitution event');
    }
  };

  // Computed values for modals
  const benchPlayersForSubstitution = substitutionModal.outgoingPlayer
    ? [...homeTeam, ...awayTeam].filter(p =>
        p.team_id === substitutionModal.outgoingPlayer.team_id &&
        !p.is_starting &&
        !p.eligibility.eligible &&
        !p.eligibility.isSubstituted &&
        !p.eligibility.isExpelled
      )
    : [];

  const eligiblePlayersForCaptain = captainTransferModal.currentCaptain
    ? [...homeTeam, ...awayTeam].filter(p =>
        p.team_id === captainTransferModal.currentCaptain.team_id &&
        p.eligibility.eligible &&
        !p.eligibility.isSubstituted &&
        !p.eligibility.isExpelled &&
        p.id !== captainTransferModal.currentCaptain.id
      )
    : [];

  return {
    // Connection
    connected,
    joined,
    viewers,

    // Match data
    match,
    matchId,
    lineupStatus,
    eventTypes,

    // Teams
    homeTeam,
    awayTeam,
    activeCounts,
    lineupLoading,

    // Clock
    clock,
    handleStartClock,
    stopClock,
    resetClock,
    setTime,
    setInjuryTime,
    changeHalf,

    // Score
    score,
    handleScoreUpdate,

    // Events
    events,
    eventTypes,
    createEvent,
    deleteEvent,
    handleQuickEvent,

    // Overlay controls
    scoreboardVisible,
    toggleScoreboard,
    scoreboardTimeout,
    handleScoreboardTimeoutChange,
    formationsVisible,
    toggleFormations,
    logoVisible,
    toggleLogo,

    // Navigation
    navigate,
    openPublicView,
    openOBSView,

    // Match status actions
    handleStartMatch,
    handleEndMatch,
    handleCancelMatch,
    captainsAssigned,

    // Modals
    confirmModal,
    setConfirmModal,
    substitutionModal,
    setSubstitutionModal,
    captainTransferModal,
    setCaptainTransferModal,
    captainAlert,
    setCaptainAlert,
    varModal,
    setVarModal,

    // Modal handlers
    handleCaptainTransfer,
    handleSubstitutionConfirm,

    // Computed modal data
    benchPlayersForSubstitution,
    eligiblePlayersForCaptain
  };
};
